import { useEffect, useState } from 'react';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { recordedAmount } from '../form/amountFields';
import { GroupMember } from '../grouping';
import { ExpenditureCategory } from '../hooks/useExpenditures';
import { conversionPreviewNote } from '../leftOut';
import { moneyOrNotRecorded } from '../notRecorded';
import { ExpenditureRow } from '../rows';
import {
  CashflowFrequency,
  calculateAnnualAmount,
  calculateMonthlyAmount,
  cashflowFrequencyOptions,
} from '../utils';
import { expenditureCategoryOptions } from './expenditureCategories';
import { CashflowModalShell } from './modal/CashflowModalShell';
import { deletionMessage } from './modal/deletion';
import { AmountField, allocationsComplete, sumAmounts } from './modal/MemberAllocations';
import { RemovedItem, useAllocations } from './modal/useAllocations';
import { SHARED_ITEM_NOTICE, isPartOfAGroup } from './modal/sharedItem';

const AMOUNT_FIELDS: AmountField[] = [{ key: 'amount', label: 'Amount' }];

export type ExpenditureSubmission = {
  category: ExpenditureCategory;
  frequency: CashflowFrequency;
  description: string;
  allocations: { clientId: string; itemId?: string; amount: number | null }[];
  groupId?: string;
  removedItemIds: RemovedItem[];
};

type ExpenditureModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (submission: ExpenditureSubmission) => Promise<void>;
  members: GroupMember[];
  defaultClientId?: string;
  initialRow?: ExpenditureRow;
  /**
   * The view behind this modal holds only one client's rows of a shared item, or cannot vouch
   * for having the rest, so the item is open in part. What every row of the group carries stays
   * read-only — see {@link SHARED_ITEM_NOTICE} — and Delete is withheld, since it would take
   * the rows in hand while the warning named only the members among them.
   */
  ownRowsOnly?: boolean;
  /** Absent where the view cannot delete; the form only offers it on an item that exists. */
  onDelete?: (items: { id: string; clientId: string }[]) => Promise<void>;
};

export function ExpenditureFormModal({
  opened,
  onClose,
  onSubmit,
  members,
  defaultClientId,
  initialRow,
  ownRowsOnly,
  onDelete,
}: ExpenditureModalProps) {
  const currency = useCurrency();
  const [category, setCategory] = useState<ExpenditureCategory | ''>('');
  const [frequency, setFrequency] = useState<CashflowFrequency | ''>('Monthly');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const seed = initialRow?.items.map(item => ({
    clientId: item.clientId,
    itemId: item.id,
    amounts: { amount: item.amount },
  }));
  const { allocations, removedItems, addMember, removeMember, setAmount } = useAllocations(
    opened,
    seed,
    defaultClientId,
  );

  useEffect(() => {
    if (!opened) return;
    setCategory((initialRow?.category as ExpenditureCategory) ?? '');
    // Detected records can arrive without a frequency, so leave it for the adviser to choose
    setFrequency(initialRow ? (initialRow.frequency ?? '') : 'Monthly');
    setDescription(initialRow?.description ?? '');
  }, [opened, initialRow]);

  const locked = Boolean(ownRowsOnly) && isPartOfAGroup(initialRow?.items);
  const chosenFrequency = frequency || null;
  const total = sumAmounts(allocations, 'amount');
  const canSubmit = !!category && !!frequency && allocationsComplete(allocations, AMOUNT_FIELDS);

  const handleSubmit = async () => {
    if (!canSubmit || isSaving || !category || !frequency) return;
    setIsSaving(true);
    try {
      await onSubmit({
        category,
        frequency,
        description,
        groupId: initialRow?.items.find(item => item.groupId)?.groupId ?? undefined,
        allocations: allocations.map(allocation => ({
          clientId: allocation.clientId,
          itemId: allocation.itemId,
          amount: recordedAmount(allocation.amounts.amount),
        })),
        removedItemIds: removedItems,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CashflowModalShell
      opened={opened}
      onClose={onClose}
      title={initialRow ? 'Edit expenditure' : 'Add expenditure'}
      submitLabel={initialRow ? 'Save expenditure' : 'Add expenditure'}
      onSubmit={handleSubmit}
      canSubmit={canSubmit}
      isSaving={isSaving}
      previewTitle="Total spend preview"
      previewNote={conversionPreviewNote(chosenFrequency)}
      previewRows={[
        {
          label: 'Spend',
          qualifier: '(monthly)',
          value: moneyOrNotRecorded(calculateMonthlyAmount(total, chosenFrequency), currency),
        },
        {
          label: 'Spend',
          qualifier: '(annually)',
          value: moneyOrNotRecorded(calculateAnnualAmount(total, chosenFrequency), currency),
        },
      ]}
      testId="add-expenditure-modal"
      fields={[
        [
          {
            kind: 'select',
            label: 'Category',
            value: category,
            onChange: value => setCategory(value as ExpenditureCategory),
            data: expenditureCategoryOptions,
            placeholder: 'Search categories',
            required: true,
            searchable: true,
            disabled: locked,
            error: initialRow && !category ? 'Choose a category' : undefined,
          },
          {
            kind: 'select',
            label: 'Frequency',
            value: frequency,
            onChange: value => setFrequency(value as CashflowFrequency),
            data: cashflowFrequencyOptions,
            required: true,
            disabled: locked,
            error: frequency ? undefined : 'Choose a frequency',
          },
        ],
        [
          {
            kind: 'text',
            label: 'Description',
            value: description,
            onChange: setDescription,
            disabled: locked,
          },
        ],
      ]}
      allocation={{
        members,
        allocations,
        amountFields: AMOUNT_FIELDS,
        amountQualifier: frequency.toLowerCase(),
        emptyLabel: 'Select at least 1 family member to add expenditure',
        membersLocked: locked,
        onAdd: addMember,
        onRemove: removeMember,
        onAmountChange: setAmount,
      }}
      notice={locked ? SHARED_ITEM_NOTICE : undefined}
      deletion={
        initialRow && onDelete && !locked
          ? {
              onDelete: () => onDelete(initialRow.items),
              title: 'Delete expenditure?',
              message: deletionMessage('expenditure', initialRow.members),
              confirmLabel: 'Delete expenditure',
            }
          : undefined
      }
    />
  );
}
